import {
  ArrowDownward,
  ArrowForward,
  ArrowUpward,
  Lock,
  LockOpen,
  SwapHoriz,
} from '@mui/icons-material'
import { ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  ArrowLeftIcon,
  BinIcon,
  EditIcon,
  HeightIcon,
  PowerLeftIcon,
  PowerRightIcon,
} from '@tom-ui/ui'
import { ConfigAreaType } from '@yard/app/api'

interface Props {
  axis: 'bay' | 'row'
  open: boolean
  anchorPosition: { top: number; left: number } | undefined
  width?: number
  onClose: () => void
  onRename: () => void
  onInsertBefore: () => void
  onInsertAfter: () => void
  onDelete: () => void
  onAdjustMaxTier: () => void
  onBlockConfigArea?: () => void
  onDeleteConfigArea?: (configAreaType: ConfigAreaType) => void
  onAddForePlugs?: () => void
  onAddAftPlugs?: () => void
  onDeleteForePlugs?: () => void
  onDeleteAftPlugs?: () => void
  onAddSpaceBetweenBays?: () => void
  onDeleteSpaceBetweenBays?: () => void
}

export const YardAxisContextMenu = (props: Props) => {
  const { t } = useTranslate()
  const {
    axis,
    open,
    width,
    anchorPosition,
    onClose,
    onRename,
    onInsertBefore,
    onInsertAfter,
    onDelete,
    onAdjustMaxTier,
    onBlockConfigArea,
    onDeleteConfigArea,
    onAddForePlugs,
    onAddAftPlugs,
    onDeleteForePlugs,
    onDeleteAftPlugs,
    onAddSpaceBetweenBays,
    onDeleteSpaceBetweenBays,
  } = props

  const isBay = axis === 'bay'

  return (
    <Menu
      slotProps={{
        paper: {
          style: {
            width,
          },
        },
      }}
      open={open}
      onClose={onClose}
      anchorReference='anchorPosition'
      anchorPosition={anchorPosition}
    >
      <MenuList>
        <MenuItem
          onClick={() => {
            onRename()
            onClose()
          }}
          className='nodrag'
        >
          <ListItemIcon>
            <EditIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>
            {isBay ? t('renameBay', 'Rename Bay') : t('renameRow', 'Rename Row')}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            onInsertBefore()
            onClose()
          }}
          className='nodrag'
        >
          <ListItemIcon>
            {isBay ? <ArrowLeftIcon fontSize='small' /> : <ArrowUpward fontSize='small' />}
          </ListItemIcon>
          <ListItemText>
            {isBay ? t('insertLeft', 'Insert Left') : t('insertUp', 'Insert Up')}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            onInsertAfter()
            onClose()
          }}
          className='nodrag'
        >
          <ListItemIcon>
            {isBay ? <ArrowForward fontSize='small' /> : <ArrowDownward fontSize='small' />}
          </ListItemIcon>
          <ListItemText>
            {isBay ? t('insertRight', 'Insert Right') : t('insertDown', 'Insert Down')}
          </ListItemText>
        </MenuItem>

        {(onAddForePlugs || onDeleteForePlugs) && (
          <MenuItem
            onClick={() => {
              onDeleteForePlugs ? onDeleteForePlugs() : onAddForePlugs?.()
              onClose()
            }}
            className='nodrag'
          >
            <ListItemIcon>
              <PowerRightIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {onDeleteForePlugs
                ? t('removeForePlugs', 'Remove FORE plugs')
                : t('addForePlugs', 'Add FORE plugs')}
            </ListItemText>
          </MenuItem>
        )}

        {(onAddAftPlugs || onDeleteAftPlugs) && (
          <MenuItem
            onClick={() => {
              onDeleteAftPlugs ? onDeleteAftPlugs() : onAddAftPlugs?.()
              onClose()
            }}
            className='nodrag'
          >
            <ListItemIcon>
              <PowerLeftIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {onDeleteAftPlugs
                ? t('removeAftPlugs', 'Remove AFT plugs')
                : t('addAftPlugs', 'Add AFT plugs')}
            </ListItemText>
          </MenuItem>
        )}

        {onAddSpaceBetweenBays && (
          <MenuItem
            onClick={() => {
              onAddSpaceBetweenBays()
              onClose()
            }}
            className='nodrag'
          >
            <ListItemIcon>
              <SwapHoriz fontSize='small' />
            </ListItemIcon>
            <ListItemText>{t('addSpaceAfterBay', 'Add space after bay')}</ListItemText>
          </MenuItem>
        )}

        {onDeleteSpaceBetweenBays && (
          <MenuItem
            onClick={() => {
              onDeleteSpaceBetweenBays()
              onClose()
            }}
            className='nodrag'
          >
            <ListItemIcon>
              <BinIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>{t('removeSpaceAfterBay', 'Remove space after bay')}</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            onDelete()
            onClose()
          }}
          className='nodrag'
        >
          <ListItemIcon>
            <BinIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('delete', 'Delete')}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            onAdjustMaxTier()
            onClose()
          }}
          className='nodrag'
        >
          <ListItemIcon>
            <HeightIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('adjustMaxTier', 'Adjust max tier')}</ListItemText>
        </MenuItem>

        {onBlockConfigArea && (
          <MenuItem
            onClick={() => {
              onBlockConfigArea()
              onClose()
            }}
            className='nodrag'
          >
            <ListItemIcon>
              <Lock fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {isBay
                ? t('restrictBayAcess', 'Restrict bay access')
                : t('restrictRowAccess', 'Restrict row access')}
            </ListItemText>
          </MenuItem>
        )}

        {onDeleteConfigArea && (
          <MenuItem
            onClick={() => {
              onDeleteConfigArea(ConfigAreaType.IsBlocked)
              onClose()
            }}
            className='nodrag'
          >
            <ListItemIcon>
              <LockOpen fontSize='small' />
            </ListItemIcon>
            <ListItemText>
              {isBay
                ? t('unrestrictBayAcess', 'Unrestrict bay access')
                : t('unrestrictRowAccess', 'Unrestrict row access')}
            </ListItemText>
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  )
}
